import { useEffect, useMemo, useState } from "react";
import { isEmpty } from "lodash";
import { useSearchParams } from "react-router-dom";
import {
  LogFinderAmountResult,
  getTxAmounts,
  createLogMatcherForAmounts
} from "@terraclassic-community/log-finder-ruleset";
import { TxInfo } from "@terra-money/terra.js";
import Pagination from "../../components/Pagination";
import FlexTable from "../../components/FlexTable";
import Card from "../../components/Card";
import Info from "../../components/Info";
import Icon from "../../components/Icon";
import Finder from "../../components/Finder";
import Loading from "../../components/Loading";
import Coin from "../../components/Coin";
import { useCurrentChain, useIsClassic } from "../../contexts/ChainsContext";
import {
  fromISOTime,
  sliceMsgType,
  splitCoinData
} from "../../scripts/utility";
import format from "../../scripts/format";
import { useLogfinderAmountRuleSet } from "../../hooks/useLogfinder";
import useRequest from "../../hooks/useRequest";
import { useGetQueryURL } from "../../queries/query";
import TxAmount from "../Tx/TxAmount";
import { transformTx } from "../Tx/transform";
import TaxRateAmount from "../Tx/TaxRateAmount";
import CsvExport from "./CSVExport";
import s from "./Txs.module.scss";

type Fee = {
  denom: string;
  amount: string;
};

export const getTxFee = (prop: Fee, isClassic?: boolean) =>
  prop &&
  `${format.amount(prop.amount)} ${format.denom(prop.denom, isClassic)}`;

const getRenderAmount = (
  amountList: string[] | undefined,
  amountArray: JSX.Element[]
) => {
  amountList?.forEach(amount => {
    const coin = splitCoinData(amount.trim());
    if (coin) {
      const { amount, denom } = coin;
      const element = <Coin amount={amount} denom={denom} />;

      amountArray.push(element);
    }
  });
};

const getAmount = (
  address: string,
  matchedMsg?: LogFinderAmountResult[][],
  rowLimit?: number
) => {
  const amountIn: JSX.Element[] = [];
  const amountOut: JSX.Element[] = [];
  matchedMsg?.forEach(matchedLog => {
    matchedLog?.forEach(log => {
      const amounts = log.transformed?.amount?.split(",");
      const sender = log.transformed?.sender;
      const recipient = log.transformed?.recipient;

      if (address === sender) {
        getRenderAmount(amounts, amountOut);
      }

      if (address === recipient) {
        getRenderAmount(amounts, amountIn);
      }
    });
  });

  //amount row limit
  if (rowLimit) {
    return [amountIn.slice(0, 3), amountOut.slice(0, 3)];
  }

  return [amountIn, amountOut];
};


interface BlockEventData {
  type: string;
  eventType: string;
  height: string;
  timestamp: string;
  id: number;
}

// Component to fetch and display block event amounts
const BlockEventAmounts = ({
  height,
  eventType,
  address,
  direction
}: {
  height: string;
  eventType: string;
  address: string;
  direction: "in" | "out";
}) => {
  const [amounts, setAmounts] = useState<JSX.Element[]>([]);
  const [loaded, setLoaded] = useState(false);
  const url = useGetQueryURL(`/v1/blocks/${height}/events`);
  // Normalize eventType to lowercase for API query (e.g., "End_block" -> "end_block")
  const scope = eventType.toLowerCase();

  useEffect(() => {
    let cancelled = false;

    const fetchEvents = async () => {
      try {
        const response = await fetch(
          `${url}?account=${address}&scope=${scope}`
        );
        if (!response.ok || cancelled) return;
        const data = await response.json();
        if (cancelled) return;

        const parsedAmounts: JSX.Element[] = [];
        // API returns { events: [...], height: number }
        const eventList = Array.isArray(data?.events) ? data.events : [];

        eventList.forEach((event: BlockEvent) => {
          // Use coin_spent/coin_received as they are universal for all coin movements
          if (event.type !== "coin_spent" && event.type !== "coin_received")
            return;

          const attrs = event.attributes.reduce((acc, attr) => {
            if (attr.key && attr.value) acc[attr.key] = attr.value;
            return acc;
          }, {} as Record<string, string>);

          const amount = attrs.amount;
          if (!amount) return;

          amount.split(",").forEach(coinStr => {
            const coin = splitCoinData(coinStr.trim());
            if (!coin) return;

            const isInflow =
              event.type === "coin_received" && attrs.receiver === address;
            const isOutflow =
              event.type === "coin_spent" && attrs.spender === address;

            if (
              (direction === "in" && isInflow) ||
              (direction === "out" && isOutflow)
            ) {
              parsedAmounts.push(
                <Coin
                  amount={coin.amount}
                  denom={coin.denom}
                  key={`${coin.denom}-${coin.amount}-${parsedAmounts.length}`}
                />
              );
            }
          });
        });

        setAmounts(parsedAmounts.slice(0, 3));
        setLoaded(true);
      } catch {
        setLoaded(true);
      }
    };

    fetchEvents();
    return () => {
      cancelled = true;
    };
  }, [url, address, scope, direction]);

  if (!loaded) return <span>...</span>;
  if (amounts.length === 0) return <span>-</span>;

  return (
    <>
      {amounts.map((amount, index) => (
        <span key={index}>
          {direction === "out" ? "-" : "+"}
          {amount}
        </span>
      ))}
    </>
  );
};

// Format block event type to nice display name (e.g., "burn" -> "Burn", "complete_unbonding" -> "CompleteUnbonding")
const formatBlockEventType = (eventType: string): string => {
  return eventType
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join("");
};

// Component to fetch and display the action type (e.g., burn, complete_unbonding)
const BlockEventAction = ({
  height,
  eventType,
  address
}: {
  height: string;
  eventType: string;
  address: string;
}) => {
  const [action, setAction] = useState<string>(formatBlockEventType(eventType));
  const url = useGetQueryURL(`/v1/blocks/${height}/events`);
  const scope = eventType.toLowerCase();

  useEffect(() => {
    let cancelled = false;

    const fetchEvents = async () => {
      try {
        const response = await fetch(
          `${url}?account=${address}&scope=${scope}`
        );
        if (!response.ok || cancelled) return;
        const data = await response.json();
        if (cancelled) return;

        const eventList = Array.isArray(data?.events) ? data.events : [];

        // Find descriptive event types (skip coin_spent, coin_received, transfer, message)
        const skipTypes = [
          "coin_spent",
          "coin_received",
          "transfer",
          "message"
        ];
        const descriptiveEvent = eventList.find(
          (event: BlockEvent) => !skipTypes.includes(event.type)
        );

        if (descriptiveEvent) {
          setAction(formatBlockEventType(descriptiveEvent.type));
        }
      } catch {
        // Keep default eventType
      }
    };

    fetchEvents();
    return () => {
      cancelled = true;
    };
  }, [url, address, scope, eventType]);

  return <span className="type">{action}</span>;
};

const getBlockEventRow = (
  event: BlockEventData,
  network: string,
  address: string
) => {
  const { eventType, height, timestamp } = event;

  return [
    <span>
      <div className={s.wrapper}>
        <Finder q="blocks" network={network} v={height}>
          {`Block #${height}`}
        </Finder>
        <Icon name="event" className={s.blockEventIcon} />
      </div>
    </span>,
    <BlockEventAction
      height={height}
      eventType={eventType}
      address={address}
    />,
    <span>
      <Finder q="blocks" network={network} v={height}>
        {height}
      </Finder>
    </span>,
    <span className={s.amount}>
      <BlockEventAmounts
        height={height}
        eventType={eventType}
        address={address}
        direction="out"
      />
    </span>,
    <span className={s.amount}>
      <BlockEventAmounts
        height={height}
        eventType={eventType}
        address={address}
        direction="in"
      />
    </span>,
    <span>{fromISOTime(timestamp)}</span>,
    <span>-</span>,
    <span>-</span>
  ];
};

const Txs = ({ address }: { address: string }) => {
  const { chainID } = useCurrentChain();
  const [searchParams, setSearchParams] = useSearchParams();
  const account2 = searchParams.get("account2")?.trim() || undefined;
  const [offset, setOffset] = useState<number>(0);
  const isClassic = useIsClassic();

  const params = {
    offset,
    limit: 100,
    account: address,
    ...(account2 ? { account2 } : {})
  };
  const url = useGetQueryURL("/v1/txs");
  const { data, isLoading } = useRequest<{
    next: number;
    txs: TxInfo[];
  }>({ url, params });

  const [txsRow, setTxsRow] = useState<JSX.Element[][]>([]);

  const ruleSet = useLogfinderAmountRuleSet();
  const logMatcher = useMemo(
    () => createLogMatcherForAmounts(ruleSet),
    [ruleSet]
  );

  useEffect(() => {
    setOffset(0);
    setTxsRow([]);
  }, [address, account2]);

  useEffect(() => {
    if (data?.txs) {
      const txRow = data.txs.map((tx: any) => {
        // Handle block events differently
        if (tx.type === "block_event") {
          return getBlockEventRow(tx, chainID, address);
        }
        const txData: TxResponse = transformTx(tx, chainID);
        const matchedLogs = getTxAmounts(
          JSON.stringify(txData),
          logMatcher,
          address
        );
        return getRow(
          txData,
          chainID,
          address,
          matchedLogs,
          isClassic
        );
      });
      setTxsRow(stack => [...stack, ...txRow]);
    }
    // eslint-disable-next-line
  }, [data, chainID, address]);

  const head = [
    `Tx hash`,
    `Type`,
    `Block`,
    `Amount (Out)`,
    `Amount (In)`,
    `Timestamp`,
    `Fee`,
    isClassic ? "Tax" : null
  ];

  return (
    <Card title="Transactions" bordered headerClassName={s.cardTitle}>
      {!isEmpty(txsRow) ? (
        <div className={s.exportCsvWrapper}>
          <CsvExport address={address} />
        </div>
      ) : null}

      {account2 ? (
        <div className={s.pairFilter}>
          Pair filter: {format.truncate(account2, [10, 10])}
          <button
            className={s.clearPairFilter}
            onClick={() => {
              const nextSearchParams = new URLSearchParams(searchParams);
              nextSearchParams.delete("account2");
              setSearchParams(nextSearchParams);
            }}
          >
            clear
          </button>
        </div>
      ) : null}

      <Pagination
        next={data?.next}
        title="transaction"
        action={setOffset}
        loading={isLoading}
      >
        <div className={s.cardBodyContainer}>
          {isEmpty(txsRow) && isLoading ? (
            <Loading />
          ) : !isEmpty(txsRow) ? (
            <FlexTable
              head={head}
              body={txsRow}
              tableStyle={{ border: "none" }}
              headStyle={{ background: "none" }}
            />
          ) : (
            <Card>
              <Info icon="info_outline" title="">
                No more transactions
              </Info>
            </Card>
          )}
        </div>
      </Pagination>
    </Card>
  );
};

export default Txs;

const getRow = (
  response: TxResponse,
  network: string,
  address: string,
  matchedMsg?: LogFinderAmountResult[][],
  isClassic?: boolean
) => {
  const { tx: txBody, txhash, height, timestamp, chainId, logs } = response;
  const isSuccess = !response.code;
  const [amountIn, amountOut] = getAmount(address, matchedMsg, 3);
  const memo = txBody?.value?.memo;
  const fee = getTxFee(txBody?.value?.fee?.amount?.[0], isClassic);
  const feeData = fee?.split(" ");

  return [
    <span>
      <div className={s.wrapper}>
        <Finder q="tx" network={network} v={txhash}>
          {format.truncate(txhash, [8, 8])}
        </Finder>
        {isSuccess ? (
          <Icon name="check" className={s.success} />
        ) : (
          <Icon name="warning" className={s.fail} />
        )}
      </div>
    </span>,
    <span className={s.meta}>
      <div className={s.metaHead}>
        <span className="type">{sliceMsgType(txBody?.value?.msg[0].type)}</span>
        {memo ? (
          <span className={s.memoIconWrap} title={memo} aria-label={memo}>
            <Icon name="chat_bubble_outline" size={15} className={s.memoIcon} />
          </span>
        ) : null}
      </div>
    </span>,
    <span>
      <Finder q="blocks" network={network} v={String(height)}>
        {String(height)}
      </Finder>
      <span style={{ marginLeft: "3px" }}>({chainId})</span>
    </span>,
    <span className={s.amount}>
      {amountOut.length
        ? amountOut.map((amount, index) => {
            if (index >= 2) {
              return <Finder q="tx" v={txhash} children="..." key={index} />;
            } else {
              return <span key={index}>-{amount}</span>;
            }
          })
        : "-"}
    </span>,
    <span className={s.amount}>
      {amountIn.length
        ? amountIn.map((amount, index) => {
            if (index >= 2) {
              return <Finder q="tx" v={txhash} children="..." key={index} />;
            } else {
              return <span key={index}>+{amount}</span>;
            }
          })
        : "-"}
    </span>,
    <span>{fromISOTime(timestamp.toString())}</span>,
    <span>
      <TxAmount
        amount={feeData?.[0]}
        denom={feeData?.[1]}
        isFormatAmount={true}
      />
    </span>,
    <span>
      <TaxRateAmount logs={logs} />
    </span>
  ];
};
